
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Facebook, Instagram, Youtube, Twitter, Link, Plus, AlertTriangle, Share2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { FacebookShareDialog } from './FacebookShareDialog';

const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  twitter: Twitter,
  tiktok: Link
};

const platformColors = {
  facebook: 'bg-blue-600',
  instagram: 'bg-pink-600',
  youtube: 'bg-red-600',
  twitter: 'bg-sky-500',
  tiktok: 'bg-black'
};

interface AddSocialAccountButtonsProps {
  connectedPlatforms: string[];
  onRefetch: () => void;
  onRefetchGroups: () => void;
}

export const AddSocialAccountButtons = ({ 
  connectedPlatforms, 
  onRefetch, 
  onRefetchGroups 
}: AddSocialAccountButtonsProps) => {
  const [connecting, setConnecting] = useState<string | null>(null);
  const [showFacebookShare, setShowFacebookShare] = useState(false);
  const { toast } = useToast();

  const handleConnect = async (platform: string) => {
    if (platform === 'facebook') {
      setShowFacebookShare(true);
      return;
    }

    setConnecting(platform);
    
    try {
      // For other platforms, show coming soon message
      toast({
        title: "Coming Soon",
        description: `${platform.charAt(0).toUpperCase() + platform.slice(1)} integration will be available soon.`,
      });
    } catch (error) {
      console.error('OAuth error:', error);
      toast({
        title: "Connection Error",
        description: "Failed to connect social media account.",
        variant: "destructive"
      });
    } finally {
      setConnecting(null);
    }
  };

  if (showFacebookShare) {
    return (
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium">Facebook Sharing</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFacebookShare(false)}
          >
            Back to Social Accounts
          </Button>
        </div>
        <FacebookShareDialog 
          title="Check out YieldSocial!"
          description="Join me on YieldSocial - the social platform for farmers and agricultural enthusiasts."
          postUrl={window.location.origin}
        />
      </div>
    );
  }

  return (
    <div className="border-t pt-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Share on Social Media</span>
      </div>
      
      <div className="flex gap-1 justify-center">
        {Object.entries(platformIcons).map(([platform, IconComponent]) => {
          const colorClass = platformColors[platform as keyof typeof platformColors];
          
          return (
            <Button
              key={platform}
              variant="outline"
              className="w-8 h-8 p-0 relative"
              onClick={() => handleConnect(platform)}
              disabled={connecting === platform}
            >
              <div className={`p-1 rounded ${colorClass} text-white`}>
                <IconComponent size={12} />
              </div>
              {platform === 'facebook' && <Share2 size={8} className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-0.5" />}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
